import axios, { AxiosError } from 'axios';

interface ApiErrorBody {
  error?: { code?: string; message?: string };
}

/**
 * The server's machine-readable error code, when there is one. Use this only
 * where the client must branch on the reason (send the user back to login, for
 * instance). For anything the user reads, use getApiErrorMessage.
 */
export function getApiErrorCode(error: unknown): string | undefined {
  if (!axios.isAxiosError(error)) return undefined;
  return (error as AxiosError<ApiErrorBody>).response?.data?.error?.code;
}

/**
 * Matches a bare error code (AUTH_INVALID_CREDENTIALS, RESOURCE_NOT_FOUND) so
 * we never render one as if it were prose. The backend now defaults every code
 * to a real sentence, but an older deployed API can still send the code as the
 * message, and no genuine user-facing message is SHOUTING_SNAKE_CASE.
 */
const ERROR_CODE_SHAPE = /^[A-Z][A-Z0-9]*(_[A-Z0-9]+)+$/;

/**
 * Turn any thrown request error into a message safe to show a user. Prefers the
 * server's structured error message. For bodyless failures (an edge proxy 5xx
 * while the backend restarts during a deploy, or a dropped connection) it
 * returns a friendly line instead of leaking axios's raw
 * "Request failed with status code 500".
 */
export function getApiErrorMessage(
  error: unknown,
  fallback = 'Something went wrong. Please try again.'
): string {
  if (!axios.isAxiosError(error)) return fallback;
  const axiosError = error as AxiosError<ApiErrorBody>;

  const serverMessage = axiosError.response?.data?.error?.message;
  if (serverMessage && !ERROR_CODE_SHAPE.test(serverMessage.trim())) return serverMessage;

  // Request was sent but no response came back: server unreachable / network.
  if (axiosError.request && !axiosError.response) {
    return 'Cannot reach the server. Check your connection and try again.';
  }

  const status = axiosError.response?.status;
  if (status && status >= 500) {
    return 'The server is temporarily unavailable. Please try again in a moment.';
  }

  return fallback;
}
